// CODE SPLITTING.
//
// Every page was a STATIC import, so all 121 of them — about 95,000 lines —
// shipped in one bundle that the browser downloaded and PARSED before
// anything rendered, whether or not the user visited them.
//
// lazy() splits each page into its own chunk, fetched when first opened.
// QuotationPage alone is 8,090 lines.
import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import {
  Box, Scissors, Package, PackageOpen,
  Building2, MapPin, Warehouse, Settings,
} from "lucide-react";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { LoadingProvider } from "./context/LoadingContext";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import TopBrandBar from "./components/TopBrandBar";
import SplashScreen from "./components/SplashScreen";
import ComingSoon from "./components/ComingSoon";
import { PageSpinner } from "./components/Spinner";
import NAV from "./theme/navConfig";

import Login from "./pages/Login";
// Phase 36da.99.o — per-cloth date-wise stock movement ledger
// Phase 36da.99.uc — UoM Chain master.
// Phase 36da.99.mv — Material Variant master.
// Phase 36da.99.bp — Purchase Invoice (vendor bill) + Vendor Ledger
// Phase 36da.72 — per-vendor / per-school manual cloth stock + breakdown report
// Phase 36da.99.bp — per-vendor / per-school manual material stock
// Phase 36by — Rate Difference module: price-adjustment credit notes.
// Sits next to GoodsReturn in the menu but uses its own table; reuses
// the GOODS_RETURN permission group so existing roles are unaffected.
// Phase 36ce — Direct Bill module: Scholar→Customer billing without Mafatlal/L2 step.
// Phase 36ca — Employee Salary Increment register.  Reuses EMPLOYEE
// permission group (same role manages staff records and their increments).

// Phase 36da.99.gj — global subscription gating UI
import PayWall from "./components/PayWall";
import GraceBanner from "./components/GraceBanner";
import SubscriptionCallbackPage from "./pages/SubscriptionCallbackPage";

import SimpleMaster from "./masters/SimpleMaster";
import {
  UOM_CONFIG, PANNA_CONFIG, GSM_CONFIG, MATCHING_CONFIG, COLOR_CONFIG,
} from "./masters/masterConfigs";

import T from "./theme/colors";
import AcceptQuotationPage from "./pages/AcceptQuotationPage";
import StitchingOperationPage from "./pages/StitchingOperationPage";
import MaterialSetPage from "./pages/MaterialSetPage";
import PaymentHubPage from "./pages/PaymentHubPage";
import LedgerHubPage from "./pages/LedgerHubPage";

// ── Lazily-loaded pages ──────────────────────────────────────────────────
// Declared BELOW every import: these are module-body statements, and eslint
// import/first rejects any import that appears after one.
//
// Login, SubscriptionCallbackPage and AcceptQuotationPage are deliberately
// NOT lazy — they render outside the <Suspense> boundary, where a lazy
// component throws.

const Dashboard = lazy(() => import("./pages/Dashboard"));
const VendorPage = lazy(() => import("./pages/VendorPage"));
const CustomerPage = lazy(() => import("./pages/CustomerPage"));
const StockPage = lazy(() => import("./pages/StockPage"));
const StockLedgerPage = lazy(() => import("./pages/StockLedgerPage"));
const MaterialStockLedgerPage = lazy(() => import("./pages/MaterialStockLedgerPage"));
const TransportPage = lazy(() => import("./pages/TransportPage"));
const HundekariPage = lazy(() => import("./pages/HundekariPage"));
const JobWorkEntryPage = lazy(() => import("./pages/JobWorkEntryPage"));
const FixedSalaryEntryPage = lazy(() => import("./pages/FixedSalaryEntryPage"));
const FixedSalaryReportPage = lazy(() => import("./pages/FixedSalaryReportPage"));
const JobWorkReportPage = lazy(() => import("./pages/JobWorkReportPage"));
const SizeChartPage = lazy(() => import("./pages/SizeChartPage"));
const SalesmanPage = lazy(() => import("./pages/SalesmanPage"));
const CustomerGroupPage = lazy(() => import("./pages/CustomerGroupPage"));
const SaleOrderPage = lazy(() => import("./pages/SaleOrderPage"));
const GarmentLabelPage = lazy(() => import("./pages/GarmentLabelPage"));
const OrderFormPage = lazy(() => import("./pages/OrderFormPage"));
const PackingSlipPage = lazy(() => import("./pages/PackingSlipPage"));
const PurchaseOrderPage = lazy(() => import("./pages/PurchaseOrderPage"));
const ClothPurchaseOrderPage = lazy(() => import("./pages/ClothPurchaseOrderPage"));
const MaterialPurchaseOrderPage = lazy(() => import("./pages/MaterialPurchaseOrderPage"));
const ItemPage = lazy(() => import("./pages/ItemPage"));
const MaterialPage = lazy(() => import("./pages/MaterialPage"));
const UomChainPage = lazy(() => import("./pages/UomChainPage"));
const MaterialVariantPage = lazy(() => import("./pages/MaterialVariantPage"));
const ClothPage = lazy(() => import("./pages/ClothPage"));
const ClothVariantPage = lazy(() => import("./pages/ClothVariantPage"));
const RackPage = lazy(() => import("./pages/RackPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const FirmPage = lazy(() => import("./pages/FirmPage"));
const PatternPage = lazy(() => import("./pages/PatternPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const GrnPage = lazy(() => import("./pages/GrnPage"));
const PurchaseInvoicePage = lazy(() => import("./pages/PurchaseInvoicePage"));
const DebitNotePage = lazy(() => import("./pages/DebitNotePage"));
const ClothGrnPage = lazy(() => import("./pages/ClothGrnPage"));
const MaterialGrnPage = lazy(() => import("./pages/MaterialGrnPage"));
const BaleOpeningPage = lazy(() => import("./pages/BaleOpeningPage"));
const QuotationPage = lazy(() => import("./pages/QuotationPage"));
const BarcodeQuotationPage = lazy(() => import("./pages/BarcodeQuotationPage"));
const SchoolPage = lazy(() => import("./pages/SchoolPage"));
const DepartmentPage = lazy(() => import("./pages/DepartmentPage"));
const CuttingIssuePage = lazy(() => import("./pages/CuttingIssuePage"));
const CuttingInwardPage = lazy(() => import("./pages/CuttingInwardPage"));
const DeptFlowPage = lazy(() => import("./pages/DeptFlowPage"));
const MaterialOutwardPage = lazy(() => import("./pages/MaterialOutwardPage"));
const EmployeePage = lazy(() => import("./pages/EmployeePage"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const AttendanceLogPage = lazy(() => import("./pages/AttendanceLogPage"));
const AttendancePolicyPage = lazy(() => import("./pages/AttendancePolicyPage"));
const OfficialHolidayPage = lazy(() => import("./pages/OfficialHolidayPage"));
const AttendanceRegisterPage = lazy(() => import("./pages/AttendanceRegisterPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const ReasonMasterPage = lazy(() => import("./pages/ReasonMasterPage"));
const SoTrackingPage = lazy(() => import("./pages/SoTrackingPage"));
const ProductSoTrackingPage = lazy(() => import("./pages/ProductSoTrackingPage"));
const UserActivityReportPage = lazy(() => import("./pages/UserActivityReportPage"));
const ProductionSheetPage = lazy(() => import("./pages/ProductionSheetPage"));
const ServiceMasterPage = lazy(() => import("./pages/ServiceMasterPage"));
const ContractorPage = lazy(() => import("./pages/ContractorPage"));
const ContractorIssuePage = lazy(() => import("./pages/ContractorIssuePage"));
const ContractorInwardPage = lazy(() => import("./pages/ContractorInwardPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const DeliveryChallanPage = lazy(() => import("./pages/DeliveryChallanPage"));
const DeliveryChallanBarcodePage = lazy(() => import("./pages/DeliveryChallanBarcodePage"));
const InvoiceRegisterPage = lazy(() => import("./pages/InvoiceRegisterPage"));
const InvoiceSettingsPage = lazy(() => import("./pages/InvoiceSettingsPage"));
const AgeingReportPage = lazy(() => import("./pages/AgeingReportPage"));
const ExpenseMasterPage = lazy(() => import("./pages/ExpenseMasterPage"));
const AllPaymentsRegisterPage = lazy(() => import("./pages/AllPaymentsRegisterPage"));
const ManualGarmentStockPage = lazy(() => import("./pages/ManualGarmentStockPage"));
const ManualClothStockPage = lazy(() => import("./pages/ManualClothStockPage"));
const ManualMaterialStockPage = lazy(() => import("./pages/ManualMaterialStockPage"));
const ClothStockReportPage = lazy(() => import("./pages/ClothStockReportPage"));
const DailyStitchingReportPage = lazy(() => import("./pages/DailyStitchingReportPage"));
const OldStockTrackingPage = lazy(() => import("./pages/OldStockTrackingPage"));
const VendorPoReportPage = lazy(() => import("./pages/VendorPoReportPage"));
const ClothVendorPoReportPage = lazy(() => import("./pages/ClothVendorPoReportPage"));
const MaterialVendorPoReportPage = lazy(() => import("./pages/MaterialVendorPoReportPage"));
const GoodsReturnPage = lazy(() => import("./pages/GoodsReturnPage"));
const GoodsReturnBarcodePage = lazy(() => import("./pages/GoodsReturnBarcodePage"));
const RateDifferencePage = lazy(() => import("./pages/RateDifferencePage"));
const EmployeeIncrementPage = lazy(() => import("./pages/EmployeeIncrementPage"));
const SubscriptionSettingsPage = lazy(() => import("./pages/SubscriptionSettingsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const MatchingPage = lazy(() => import("./pages/MatchingPage"));


// Flatten NAV once at module load for O(1) guard lookups by hash id.
const NAV_ITEM_BY_ID = Object.fromEntries(
  NAV.flatMap(g => g.items).map(it => [it.id, it])
);

// ─── Page resolver ──────────────────────────────────────────────────────
function renderPage(page) {
  switch (page) {
    /* ── Core pages ── */
    case "dashboard": return <Dashboard />;
    case "vendors": return <VendorPage />;
    case "customers": return <CustomerPage />;
    case "customer-groups": return <CustomerGroupPage />;
    case "size-chart": return <SizeChartPage />;
    case "stock": return <StockPage />;
    case "stock-ledger": return <StockLedgerPage />;
    case "material-stock-ledger": return <MaterialStockLedgerPage />;
    case "manual-garment-stock": return <ManualGarmentStockPage />;
    // Phase 36da.72 — manual cloth stock register + breakdown report.
    // Both behind the MANUAL_CLOTH_STOCK module permission (set in navConfig).
    case "manual-cloth-stock": return <ManualClothStockPage />;
    case "cloth-stock-report": return <ClothStockReportPage />;
    // Phase 36da.99.dsr — Daily Stitching Report.
    case "daily-stitching-report": return <DailyStitchingReportPage />;
    // Phase 36da.99.bp — manual material stock register, mirror of cloth.
    // Renamed from "manual-material-stock". The old id is kept as an alias so
    // a bookmarked link or a saved tab does not land on a blank screen.
    case "manual-material-entry":
    case "manual-material-stock": return <ManualMaterialStockPage />;
    case "stitching-operations": return <StitchingOperationPage />;
    case "material-sets": return <MaterialSetPage />;
    case "goods-return": return <GoodsReturnPage />;
    case "gr-barcode-register": return <GoodsReturnBarcodePage />;
    case "rate-difference": return <RateDifferencePage />;
    case "employee-increment": return <EmployeeIncrementPage />;
    case "job-work-entry": return <JobWorkEntryPage />;
    case "fixed-salary-entry": return <FixedSalaryEntryPage />;
    case "job-work-report": return <JobWorkReportPage />;
    case "fixed-salary-report": return <FixedSalaryReportPage />;
    case "expense-masters": return <ExpenseMasterPage />;
    case "payment-hub": return <PaymentHubPage />;
    case "ledger-hub": return <LedgerHubPage />;
    case "all-payments-register": return <AllPaymentsRegisterPage />;
    case "transport": return <TransportPage />;
    case "hundekari": return <HundekariPage />;
    case "salesmen": return <SalesmanPage />;
    case "order-forms": return <OrderFormPage />;
    case "sale-orders": return <SaleOrderPage />;
    case "garment-labels": return <GarmentLabelPage />;
    case "packing-slips": return <PackingSlipPage />;
    case "delivery-challans": return <DeliveryChallanPage />;
    case "dc-barcode-register": return <DeliveryChallanBarcodePage />;
    case "invoice-register-l1": return <InvoiceRegisterPage key="inv-l1" level="L1" />;
    case "ageing-report-l1": return <AgeingReportPage key="age-l1" level="L1" />;
    case "invoice-settings": return <InvoiceSettingsPage />;
    case "purchase-orders": return <PurchaseOrderPage />;
    case "cloth-purchase-orders": return <ClothPurchaseOrderPage />;
    case "material-purchase-orders": return <MaterialPurchaseOrderPage />;

    /* ── Simple masters (generic CRUD) ── */
    case "uom": return <SimpleMaster key="uom"      {...UOM_CONFIG} />;
    // Phase 36da.99.uc — UoM Chain master page.
    case "uom-chain": return <UomChainPage />;
    case "panna": return <SimpleMaster key="panna"    {...PANNA_CONFIG} />;
    case "gsm": return <SimpleMaster key="gsm"        {...GSM_CONFIG} />;
    case "matching": return <MatchingPage />;
    case "color": return <SimpleMaster key="color"    {...COLOR_CONFIG} />;
    case "schools": return <SchoolPage />;
    case "departments": return <DepartmentPage />;
    case "cutting-issues": return <CuttingIssuePage />;
    case "cutting-inwards": return <CuttingInwardPage />;
    case "dept-flow": return <DeptFlowPage />;
    case "material-outward": return <MaterialOutwardPage />;
    case "employees": return <EmployeePage />;
    case "attendance": return <AttendancePage />;
    case "attendance-log": return <AttendanceLogPage />;
    case "attendance-register": return <AttendanceRegisterPage />;
    case "attendance-policy": return <AttendancePolicyPage />;
    case "official-holidays": return <OfficialHolidayPage />;
    case "brands": return <BrandPage />;
    case "services": return <ServiceMasterPage />;
    case "reasons": return <ReasonMasterPage />;
    case "so-tracking": return <SoTrackingPage />;
    case "product-so-tracking": return <ProductSoTrackingPage />;
    case "user-activity-report": return <UserActivityReportPage />;
    case "production-sheet": return <ProductionSheetPage />;
    case "old-stock-tracking": return <OldStockTrackingPage />;
    case "vendor-po-report": return <VendorPoReportPage />;
    case "vendor-po-report-cloth": return <ClothVendorPoReportPage />;
    case "vendor-po-report-material": return <MaterialVendorPoReportPage />;
    case "contractors": return <ContractorPage />;
    case "contractor-issues": return <ContractorIssuePage />;
    case "contractor-inwards": return <ContractorInwardPage />;
    case "settings": return <SettingsPage />;
    case "subscription": return <SubscriptionPage />;
    case "subscription-settings": return <SubscriptionSettingsPage />;

    /* ── Complex masters ── */
    case "items": return <ItemPage />;
    case "material": return <MaterialPage />;
    // Phase 36da.99.mv — Material Variant master page.
    case "material-variants": return <MaterialVariantPage />;

    /* ── Coming soon ── */
    case "cloth": return <ClothPage />;
    case "cloth-variants": return <ClothVariantPage />;
    case "patterns": return <PatternPage />;
    case "products": return <ProductPage />;
    case "grn": return <GrnPage />;
    // Phase 36da.99.bp — Purchase Invoice + Vendor Ledger + Vendor Payment
    case "purchase-invoices": return <PurchaseInvoicePage />;
    case "debit-notes": return <DebitNotePage />;
    case "cloth-grn": return <ClothGrnPage />;
    case "material-grn": return <MaterialGrnPage />;
    case "bale-opening": return <BaleOpeningPage />;
    case "quotations": return <QuotationPage />;
    case "barcode-quotations": return <BarcodeQuotationPage />;

    case "firms": return <FirmPage />;
    case "locations": return <LocationPage />;
    case "racks": return <RackPage />;
    case "users": return <UsersPage />;

    default:
      return <ComingSoon title={page} icon={Settings} subtitle="This module is coming soon." />;
  }
}

// ─── Protected layout ───────────────────────────────────────────────────
function AppLayout() {
  const { canView, getFirstAllowedPage } = useAuth();

  // Decide the initial page when the hash is empty or points nowhere real.
  //   - If the user has DASHBOARD:VIEW, land on dashboard (default experience).
  //   - Otherwise drop them on their first allowed sidebar item.
  // This runs once at mount and whenever the hash changes.
  const resolveInitialPage = useCallback(() => {
    const raw = (window.location.hash || "").replace("#", "").trim();
    if (raw) return raw;                           // honour explicit deep link
    return canView("DASHBOARD") ? "dashboard" : getFirstAllowedPage();
  }, [canView, getFirstAllowedPage]);

  const [page, setPage] = useState(resolveInitialPage);
  const [collapsed, setCollapsed] = useState(false);
  const { themeKey } = useTheme();

  // Sync hash → state (browser back/forward)
  useEffect(() => {
    const onHash = () => setPage(resolveInitialPage());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [resolveInitialPage]);

  // Route guard — if the user deep-links (or was persisted onto) a page
  // their permissions don't cover, redirect to the first page they CAN view.
  //
  // Dashboard gets the same guard as every other module now: if the user
  // lacks DASHBOARD:VIEW, we must not bounce them back to dashboard
  // (would loop) — so we resolve a real allowed page instead.
  useEffect(() => {
    const item = NAV_ITEM_BY_ID[page];
    if (item && item.module && !canView(item.module)) {
      const target = getFirstAllowedPage();
      if (target !== page) {                        // guard against redirect loops
        window.location.hash = target;
        setPage(target);
      }
    }
  }, [page, canView, getFirstAllowedPage]);

  // Navigate: update both state and hash
  const navigate = useCallback((p) => {
    window.location.hash = p;
    setPage(p);
  }, []);

  return (
    <div key={themeKey} style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: T.bgApp }}>
      <GraceBanner />
      <TopBrandBar />
      <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        <Sidebar
          active={page}
          onNavigate={navigate}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(c => !c)}
        />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
          <TopBar page={page} />
          <main style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            {/* Suspense is REQUIRED for lazy() — without it React throws when a
                chunk is still loading. The fallback is deliberately plain: a
                page chunk arrives in well under a second on any normal
                connection, and a spinner that flashes is worse than a line of
                text that does not. */}
            <Suspense fallback={
              <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 13 }}>
                Loading…
              </div>
            }>
              {renderPage(page)}
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}

// ─── Auth gate ──────────────────────────────────────────────────────────
function AuthGate() {
  const { isAuthenticated, loading, subscription, user } = useAuth();

  // Phase 36da.99.bp — show the splash again on a fresh login.  The
  // root-level splash (in <App>) already covers cold app boot; this
  // second splash specifically fires the moment isAuthenticated flips
  // false → true (i.e. the user just submitted Login).  Page refreshes
  // while already authenticated do NOT trigger it again — that case is
  // covered by the root splash.
  const [loginSplash, setLoginSplash] = useState(false);
  const prevAuth = useRef(isAuthenticated);
  useEffect(() => {
    if (!prevAuth.current && isAuthenticated) {
      setLoginSplash(true);
    }
    prevAuth.current = isAuthenticated;
  }, [isAuthenticated]);

  // PhonePe redirect lands here — verify the payment, then route back in.
  if (window.location.pathname.startsWith("/subscription/callback")) {
    return <SubscriptionCallbackPage />;
  }

  // Public quotation accept link — no login needed
  const acceptToken = new URLSearchParams(window.location.search).get("accept");
  if (acceptToken) {
    return <AcceptQuotationPage token={acceptToken} />;
  }

  if (loading) {
    return (
      <div style={{
        height: "100vh", display: "flex",
        alignItems: "center", justifyContent: "center",
        background: T.bgApp,
      }}>
        <PageSpinner />
      </div>
    );
  }

  if (!isAuthenticated) return <Login />;

  // Global subscription gate — LOCKED swaps the whole app for the paywall.
  // (They stay logged in so an admin can pay; GRACE shows a banner instead.)
  // SUPER_ADMIN IS EXEMPT — the same rule the server enforces.
  //
  // The lock stops a customer using unpaid software; it must not stop us
  // getting in to fix or unlock the instance, which is exactly when it is
  // needed. COMPANY_OWNER is NOT exempt: the owner is the customer, and
  // paying is what lifts the lock. They still see the PayWall, with the
  // Pay button their role already allows.
  //
  // Cosmetic only — the server gate is the real enforcement. Hiding the
  // paywall without the server exemption would just show a working UI
  // whose every request 402s.
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  if (subscription?.status === "LOCKED" && !isSuperAdmin) return <PayWall />;

  // Render the layout as before; the splash, when active, paints itself
  // as a full-screen overlay above everything (z-index 99999) so the
  // app underneath is already mounting and ready when the splash fades.
  return (
    <>
      {loginSplash && <SplashScreen onFinished={() => setLoginSplash(false)} />}
      <AppLayout />
    </>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────
export default function App() {
  // Phase 36da.99.bp — one-shot splash screen on initial mount.  Lives
  // ABOVE the providers so it appears instantly while Auth / Theme /
  // Toast / Loading contexts are still wiring up.  Once SplashScreen
  // calls back onFinished, the rest of the app renders normally; the
  // splash never shows again for this tab session.
  const [splashDone, setSplashDone] = useState(false);

  return (
    <>
      {!splashDone && <SplashScreen onFinished={() => setSplashDone(true)} />}
      <ThemeProvider>
        <LoadingProvider>
          <ToastProvider>
            <AuthProvider>
              <AuthGate />
            </AuthProvider>
          </ToastProvider>
        </LoadingProvider>
      </ThemeProvider>
    </>
  );
}